import React, { useState, useEffect, useCallback, useRef } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./styles";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { Campaign } from "model/campaign";
import { withMediaQuery, WithMediaQuery } from "component/shared/with-media-query";
import { DynamicContentType } from "type/dynamic-content";
import { updateCampaign } from "redux/slice/campaigns";
import { getMarketsOptimized, getSortedCampaignDisplayCategories } from "redux/selector";
import * as yup from "yup";
import { validate } from "shared/yup";
import { EmailMarketingType } from "type/email-marketing-type";
import { CampaignForm } from "../campaign-form";
import MuiAlert from "@mui/material/Alert";

interface Props extends
	OwnProps,
	WithMediaQuery,
	WithStyles<typeof styles>
{
}

interface OwnProps {
	campaign: Campaign;
	onSave?: () => void;
	// MAX-4201: scroll Smart Content Source into view on open (arriving via "Go to Settings").
	focusMarket?: boolean;
	onMarketFocused?: () => void;
}

const Component: React.FC<Props> = ({ campaign, onSave, focusMarket, onMarketFocused }) => {
	const [submitted, setSubmitted] = useState(false);
	const [showSavedToast, setShowSavedToast] = useState(false);
	// The save can finish after the agent has left the tab; don't touch state once unmounted.
	const mounted = useRef(true);
	useEffect(() => () => {
		mounted.current = false;
	}, []);
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [dynamicContentType, setDynamicContentType] = useState<DynamicContentType | undefined>(DynamicContentType.LEAD_PROFILE);
	const [emailBranding, setEmailBranding] = useState(false);
	const [marketId, setMarketId] = useState<number | undefined>(undefined);
	const [categoryId, setCategoryId] = useState<number | null | undefined>(undefined);

	const markets = useSelector(getMarketsOptimized);
	const categories = useSelector(getSortedCampaignDisplayCategories);
	const dispatch = useAppThunkDispatch();

	const validationSchema = {
		name: yup.string().required("Name is required."),
	};

	useEffect(() => {
		if (campaign) {
			setName(campaign.name);
			setDescription(campaign.description || "");
			setEmailBranding(campaign.emailBrandingYn || false);
			setDynamicContentType(campaign.campaignType);
			setMarketId(campaign.marketId);
			setCategoryId(campaign.campaignDisplayCategoryId ?? null);
		}
	}, [campaign]);

	const handleCancel = useCallback(() => {
		if (campaign) {
			setName(campaign.name);
			setDescription(campaign.description || "");
			setEmailBranding(campaign.emailBrandingYn || false);
			setDynamicContentType(campaign.campaignType);
			setMarketId(campaign.marketId);
			setCategoryId(campaign.campaignDisplayCategoryId ?? null);
		}
		setSubmitted(false);
	}, [campaign]);

	const handleSave = useCallback(() => {
		const state = { name, description, dynamicContentType, emailBranding, marketId };
		const errors = validate(validationSchema, state);
		if (!errors) {
			const updatedCampaign: Campaign = {
				...campaign,
				name,
				description,
				emailBrandingYn: emailBranding,
				campaignType: dynamicContentType,
				marketId,
				campaignDisplayCategoryId: categoryId ?? null,
				emailMarketingType: EmailMarketingType.DRIP,
			};
			// MAX-4201: confirm the save. Saving is allowed even without a Market (only activation
			// is blocked), so the toast also tells the agent the change stuck.
			dispatch(updateCampaign({ campaign: updatedCampaign }))
				.unwrap()
				.then(() => {
					if (mounted.current) {
						setShowSavedToast(true);
					}
				})
				.catch(() => {});
			if (onSave) {
				onSave();
			}
		}
	}, [name, description, dynamicContentType, emailBranding, marketId, categoryId, campaign, dispatch, onSave, validationSchema]);

	const state = { name, description, dynamicContentType, emailBranding, marketId };
	const errors = validate(validationSchema, state);

	return (
		<>
			<CampaignForm
				name={name}
				description={description}
				dynamicContentType={dynamicContentType}
				emailBranding={emailBranding}
				marketId={marketId}
				markets={markets}
				categoryId={categoryId}
				categories={categories}
				nameError={submitted && !!errors?.name}
				scrollToMarket={focusMarket}
				onScrolledToMarket={onMarketFocused}
				onNameChange={setName}
				onDescriptionChange={setDescription}
				onDynamicContentTypeChange={setDynamicContentType}
				onEmailBrandingChange={setEmailBranding}
				onMarketIdChange={setMarketId}
				onCategoryChange={setCategoryId}
			/>
			<div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
				<Mui.Button
					variant="outlined"
					color="primary"
					onClick={handleCancel}
				>
					Cancel
				</Mui.Button>
				<Mui.Button
					variant="contained"
					color="primary"
					onClick={() => {
						setSubmitted(true);
						handleSave();
					}}
				>
					Save Changes
				</Mui.Button>
			</div>
			<Mui.Snackbar
				open={showSavedToast}
				autoHideDuration={4000}
				onClose={() => setShowSavedToast(false)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<MuiAlert severity="success" onClose={() => setShowSavedToast(false)}>
					<Mui.Typography>Your changes have been saved</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</>
	);
};

export const EditCampaign = withMediaQuery("(max-width:600px)")(withStyles(Component, styles));
